import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { APP_CONFIG } from '@/config/app';
import { SEO_CONFIG } from '@/config/seo';
import { LandingHeader } from '@/components/landing/LandingHeader';
import { LandingFooter } from '@/components/landing/LandingFooter';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { LabelChip } from '@/components/aura';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { AnimatedSection } from '@/components/landing/AnimatedSection';
import { CTABanner } from '@/components/landing/CTABanner';
import {
  glossaryTerms,
  glossaryTermsBySlug,
  type GlossaryTerm as GlossaryTermType,
} from '@/constants/glossaryTerms';

const card = { background: 'var(--g5-card)', border: '1px solid var(--g5-line)' } as const;
const ink = { color: 'var(--g5-ink)' } as const;
const sub = { color: 'var(--g5-sub)' } as const;

/* ─── Small term card for related/suggested terms ─── */
function RelatedTermCard({ term }: { term: GlossaryTermType }) {
  const preview = term.definition.split('. ').slice(0, 1).join('. ') + '.';
  return (
    <Link to={`/glossary/${term.slug}`} className="group block h-full">
      <div className="aura-card h-full rounded-3xl p-5" style={card}>
        <div className="mb-1.5 flex flex-wrap items-start gap-2">
          <h3 className="text-sm font-bold leading-snug tracking-tight" style={ink}>
            {term.term}
          </h3>
          {term.abbreviation && (
            <span
              className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ background: 'var(--g5-tint)', color: 'var(--g5-accent)' }}
            >
              {term.abbreviation}
            </span>
          )}
        </div>
        <p className="line-clamp-2 text-xs leading-relaxed" style={sub}>{preview}</p>
      </div>
    </Link>
  );
}

export default function GlossaryTerm() {
  const { slug } = useParams<{ slug: string }>();
  const term = slug ? glossaryTermsBySlug.get(slug) : undefined;

  // 404 state
  if (!term) {
    return (
      <>
        <Helmet defer={false}>
          <title>Term Not Found | {APP_CONFIG.name}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="ashlar-public min-h-screen flex flex-col">
          <LandingHeader />
          <main className="flex-1 container mx-auto px-4 py-20 text-center">
            <BookOpen
              className="mx-auto mb-4 h-12 w-12"
              style={{ color: 'color-mix(in srgb, var(--g5-sub) 40%, transparent)' }}
            />
            <h1 className="mb-4 font-display text-2xl font-extrabold tracking-tight" style={ink}>Term Not Found</h1>
            <p className="mb-6 leading-relaxed" style={sub}>
              The glossary term you're looking for doesn't exist.
            </p>
            <Link to="/glossary" className="aura-link underline">
              <ArrowLeft className="mr-1 inline h-4 w-4" />
              Back to Glossary
            </Link>
          </main>
          <LandingFooter />
        </div>
      </>
    );
  }

  const canonicalUrl = `${SEO_CONFIG.baseUrl}/glossary/${term.slug}`;
  const fullTitle = `${term.term} — Appraisal Glossary | ${APP_CONFIG.name}`;

  // Related terms
  const relatedTerms = term.relatedTermSlugs
    .map((s) => glossaryTermsBySlug.get(s))
    .filter(Boolean) as GlossaryTermType[];

  // More from same category (excluding current + related)
  const relatedSlugs = new Set([term.slug, ...term.relatedTermSlugs]);
  const moreSameCategory = glossaryTerms
    .filter((t) => t.category === term.category && !relatedSlugs.has(t.slug))
    .slice(0, 4);

  // JSON-LD: DefinedTerm schema
  const definedTermSchema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    name: term.term,
    description: term.definition,
    url: canonicalUrl,
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: 'Real Estate Appraisal Glossary',
      url: `${SEO_CONFIG.baseUrl}/glossary`,
    },
  };

  // JSON-LD: FAQPage schema for rich snippet eligibility
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `What is ${term.abbreviation ? `${term.term}` : term.term}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: term.extendedDescription || term.definition,
        },
      },
    ],
  };

  // 3-level breadcrumb schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SEO_CONFIG.baseUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Glossary',
        item: `${SEO_CONFIG.baseUrl}/glossary`,
      },
      { '@type': 'ListItem', position: 3, name: term.term, item: canonicalUrl },
    ],
  };

  return (
    <>
      <Helmet defer={false}>
        <title>{fullTitle}</title>
        <meta name="description" content={term.definition} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={term.definition} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SEO_CONFIG.siteName} />
        <meta property="og:locale" content={SEO_CONFIG.locale} />
        <meta property="og:image" content={SEO_CONFIG.defaultOgImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={term.definition} />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(definedTermSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="ashlar-public min-h-screen flex flex-col">
        <LandingHeader />

        <main className="flex-1">
          {/* 3-level Breadcrumb */}
          <div style={{ background: 'var(--g5-soft)', borderBottom: '1px solid var(--g5-line)' }}>
            <div className="container mx-auto px-4 py-3">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/">Home</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/glossary">Glossary</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{term.term}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </div>

          {/* Content */}
          <article className="container mx-auto max-w-3xl px-4 py-12">
            <AnimatedSection>
              {/* Back link */}
              <Link
                to="/glossary"
                className="mb-6 inline-flex items-center gap-1 text-sm text-[color:var(--g5-sub)] transition-colors duration-150 hover:text-[color:var(--g5-accent)]"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Glossary
              </Link>

              {/* Badges */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <LabelChip>{term.category}</LabelChip>
                {term.abbreviation && (
                  <span
                    className="rounded-full px-3 py-1.5 text-xs font-semibold"
                    style={{ background: 'var(--g5-soft)', border: '1px solid var(--g5-line)', color: 'var(--g5-sub)' }}
                  >
                    {term.abbreviation}
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="mb-6 font-display text-3xl font-extrabold tracking-tight md:text-4xl" style={ink}>
                {term.term}
              </h1>

              {/* Definition callout */}
              <div
                className="mb-8 rounded-2xl p-6"
                style={{ background: 'var(--g5-tint)', borderLeft: '3px solid var(--g5-accent)' }}
              >
                <p className="text-base leading-relaxed" style={ink}>{term.definition}</p>
              </div>

              {/* Extended description */}
              {term.extendedDescription && (
                <div className="mb-12 max-w-none">
                  <p className="leading-relaxed" style={sub}>
                    {term.extendedDescription}
                  </p>
                </div>
              )}
            </AnimatedSection>

            {/* Related Terms */}
            {relatedTerms.length > 0 && (
              <AnimatedSection delay={100}>
                <div className="mb-8 pt-8" style={{ borderTop: '1px solid var(--g5-line)' }}>
                  <h2 className="mb-4 font-display text-xl font-extrabold tracking-tight" style={ink}>Related Terms</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {relatedTerms.map((rt) => (
                      <RelatedTermCard key={rt.slug} term={rt} />
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            )}

            {/* More from same category */}
            {moreSameCategory.length > 0 && (
              <AnimatedSection delay={150}>
                <div className="pt-8" style={{ borderTop: '1px solid var(--g5-line)' }}>
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-display text-xl font-extrabold tracking-tight" style={ink}>
                      More in {term.category}
                    </h2>
                    <Link to="/glossary" className="aura-link flex items-center gap-1 text-sm">
                      View all <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {moreSameCategory.map((t) => (
                      <RelatedTermCard key={t.slug} term={t} />
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            )}
          </article>

          {/* CTA */}
          <CTABanner
            heading="Ready to Get Started?"
            subtext="Join the modern appraisal platform — flexible pricing that scales with your practice."
            secondaryLabel="View Pricing"
            secondaryHref="/pricing"
          />
        </main>

        <LandingFooter />
      </div>
    </>
  );
}
